import { useApolloClient } from '@apollo/client';
import { useCallback, useState } from 'react';

import { createFavoritesCacheUpdater as createDisplayFavoritesCacheUpdater } from 'src/shared/graphql/cache/AiDisplayImage';
import {
  createAiDisplayImageCacheUpdater,
  createProjectCacheUpdater,
  deleteProjectAssetCacheUpdater,
} from 'src/shared/graphql/cache/Project';
import type {
  AiDisplayImage,
  AiDisplayImageFragment,
  AiGeneratedImageFragment,
  AiGeneratedImageQuery,
  AiGeneratedImageQueryVariables,
  AiOriginalImagesQuery,
  Maybe,
  Project,
  ProjectFragment,
} from 'src/shared/graphql/generated';
import {
  AiGeneratedImageDocument,
  useCreateAiDisplayImageMutation,
  useCreateProjectMutation,
  useDeleteProjectAssetMutation,
  useFavoriteAiCustomizedImageMutation,
  useGetProjectsQuery,
  useUpdateAiDisplayImageMutation,
} from 'src/shared/graphql/generated';
import { logError } from 'src/shared/telemetry/logger';

import { FAVORITES_PAGE_SIZE } from '../../constants';
import { CreatedFrom, OriginalImageStatus } from '../../static_values';
import { isSample } from '../../types';

export const MAX_PROJECT_NAME_CHARACTERS = 50;

export const formatSecondaryTextForDisplayImage = (
  customizedText: string,
  dateCreated: string,
  aiGeneratedImage?: AiGeneratedImageFragment,
) =>
  !aiGeneratedImage
    ? ''
    : (aiGeneratedImage.edits && customizedText) ||
      aiGeneratedImage.style ||
      dateCreated;

export const formatSpaceType = (spaceType?: string, capitalize?: boolean) => {
  if (spaceType) {
    const uncapitalizedSpaceType = spaceType.replace(/_/g, ' ');
    const formattedSpaceType = capitalize
      ? uncapitalizedSpaceType
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : uncapitalizedSpaceType.charAt(0).toUpperCase() +
        uncapitalizedSpaceType.slice(1);

    return formattedSpaceType;
  }

  return '';
};

export const useFavoriteCustomizedImage = (favorited?: boolean) => {
  const [favoriteCustomizedImage] = useFavoriteAiCustomizedImageMutation();
  const client = useApolloClient();

  return useCallback(
    async (aiGeneratedImage: AiGeneratedImageFragment) => {
      const newFavoriteState = favorited ?? !aiGeneratedImage.favorite;
      const favoritedAt = new Date().toISOString();

      const optimisticResponse = {
        favoriteAiCustomizedImage: {
          id: -1,
          aiOriginalImageId: aiGeneratedImage.aiOriginalImageId,
          createdFrom: CreatedFrom.FAVORITE,
          favoritedAt,
          aiGeneratedImage: {
            ...aiGeneratedImage,
            favorite: newFavoriteState,
            updatedAt: new Date().toISOString(),
          },
        },
      };

      client.writeQuery<AiGeneratedImageQuery, AiGeneratedImageQueryVariables>({
        query: AiGeneratedImageDocument,
        variables: {
          id: String(aiGeneratedImage.id),
        },
        data: {
          aiGeneratedImage: {
            ...aiGeneratedImage,
            favorite: newFavoriteState,
          },
        },
      });

      const updateDisplay = createDisplayFavoritesCacheUpdater({
        perPage: FAVORITES_PAGE_SIZE,
      });

      const favoriteResult = await favoriteCustomizedImage({
        variables: {
          aiGeneratedImageId: aiGeneratedImage.id,
          favorited: newFavoriteState,
        },
        optimisticResponse,
        update: (cache, result) => {
          if (!result.data?.favoriteAiCustomizedImage) return undefined;

          return updateDisplay(cache, {
            data: {
              updateAiDisplayImage: result?.data?.favoriteAiCustomizedImage,
            },
          });
        },
      });

      const { errors } = favoriteResult;
      if (errors?.length) errors.forEach(error => logError(error));

      return favoriteResult;
    },
    [client, favoriteCustomizedImage, favorited],
  );
};

export const useFavoriteDisplayImage = () => {
  const [updateAiDisplayImage] = useUpdateAiDisplayImageMutation();

  return useCallback(
    async (displayImage: AiDisplayImageFragment) => {
      const newFavoriteState = !displayImage.favoritedAt;
      const optimisticResponse = {
        updateAiDisplayImage: {
          ...displayImage,
          aiGeneratedImage: {
            ...displayImage.aiGeneratedImage,
            favorite: newFavoriteState,
            updatedAt: new Date().toISOString(),
          },
          favoritedAt: newFavoriteState ? new Date().toISOString() : null,
        },
      };

      const update = createDisplayFavoritesCacheUpdater({
        perPage: FAVORITES_PAGE_SIZE,
      });

      const favoriteResult = await updateAiDisplayImage({
        variables: {
          id: displayImage.id,
          favorited: newFavoriteState,
        },
        optimisticResponse,
        update,
      });

      const { errors } = favoriteResult;
      if (errors?.length) errors.forEach(error => logError(error));

      return favoriteResult;
    },
    [updateAiDisplayImage],
  );
};

export const useCreateProjectAndUpdateCache = () => {
  const [createProject] = useCreateProjectMutation();
  return useCallback(
    async (name: string, orgId?: number) => {
      const createProjectResult = await createProject({
        variables: {
          name,
          orgId,
        },
        update: createProjectCacheUpdater,
      });

      const { errors } = createProjectResult;
      if (errors?.length) errors.forEach(error => logError(error));

      return createProjectResult;
    },
    [createProject],
  );
};

export const useProjectNamingValidation = () => {
  const { data } = useGetProjectsQuery();
  const [isTooLong, setIsTooLong] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [validatedName, setValidatedName] = useState('');
  const projects = data?.projects || [];
  const projectNames = projects.map(project => project.name);

  // adds a number to the end of the name if it's duplicated
  const validateProjectName = useCallback(
    (name: string) => {
      if (!name) {
        setIsEmpty(true);
        return;
      }
      setIsEmpty(false);

      if (name.length > MAX_PROJECT_NAME_CHARACTERS) {
        setIsTooLong(true);
      } else setIsTooLong(false);

      let newName = name;
      let i = 1;
      while (projectNames.includes(newName)) {
        newName = `${name} (${i})`;
        i += 1;
      }

      setValidatedName(newName);
    },
    [projectNames],
  );

  return [validateProjectName, { isEmpty, isTooLong, validatedName }] as const;
};

export const useCreateAiDisplayImageAndUpdateCache = () => {
  const [createAiDisplayImage] = useCreateAiDisplayImageMutation();
  return useCallback(
    async (aiGeneratedImage: AiGeneratedImageFragment, projectId?: number) => {
      const { aiOriginalImageId, id: aiGeneratedImageId } = aiGeneratedImage;
      const projectsCacheUpdater = createAiDisplayImageCacheUpdater({
        projectIdToUpdate: projectId,
      });
      const createAiDisplayImageResult = await createAiDisplayImage({
        variables: {
          aiOriginalImageId: aiOriginalImageId.toString(),
          aiGeneratedImageId: aiGeneratedImageId.toString(),
          createdFrom: CreatedFrom.PROJECT,
          projectId: projectId?.toString(),
          style: aiGeneratedImage.style,
        },
        update: projectsCacheUpdater,
        refetchQueries: ['AiOriginalImage'],
      });

      const { errors } = createAiDisplayImageResult;
      if (errors?.length) errors.forEach(error => logError(error));

      return createAiDisplayImageResult;
    },
    [createAiDisplayImage],
  );
};

export const useDeleteAiDisplayImageAndUpdateCache = () => {
  const [deleteProjectAsset] = useDeleteProjectAssetMutation();
  return useCallback(
    async (addableId: number, projectId: number) => {
      const deleteProjectAssetResult = await deleteProjectAsset({
        variables: {
          addableId,
          projectId,
        },
        update: deleteProjectAssetCacheUpdater,
        refetchQueries: ['AiOriginalImage'],
      });

      const { errors } = deleteProjectAssetResult;
      if (errors?.length) errors.forEach(error => logError(error));

      return deleteProjectAssetResult;
    },
    [deleteProjectAsset],
  );
};

export const sortAiOriginalImages = (
  data: AiOriginalImagesQuery | undefined,
) => {
  if (!data) return [];

  const { aiOriginalImages: images } = data;

  if (!images) return [];
  const doneImages = images.filter(
    image => isSample(image) || image.status !== OriginalImageStatus.FAILED,
  );
  const sortedImages = doneImages
    ?.slice()
    .sort((a, b) => (b.createdAt! > a.createdAt! ? 1 : -1));

  return sortedImages;
};

// Determine if a given image is associated with (or "added" to) a given project
export const imageIsAddedToProject = (
  project?: ProjectFragment,
  generatedImageId?: number,
) => {
  if (!project || !generatedImageId) return false;
  return project.addables
    ?.filter(({ __typename }) => __typename === 'AiDisplayImage')
    .map(({ aiGeneratedImage: { id } }) => id)
    .includes(generatedImageId);
};

export const imageIsAddedToAnyProject = (
  allProjects: ProjectFragment[],
  generatedImageId?: number,
) =>
  allProjects
    .map(project => imageIsAddedToProject(project, generatedImageId))
    .some(Boolean);

export const getMostRecentAddable = (
  addables: Maybe<AiDisplayImage[]> | undefined,
): AiDisplayImage | undefined =>
  addables
    ?.filter(addable => addable.__typename === 'AiDisplayImage')
    ?.sort((a, b) => b.id - a.id)[0];

export const hasExteriorPhoto = (
  addables: Maybe<AiDisplayImage[]> | undefined,
): boolean =>
  addables?.some(
    addable =>
      addable.__typename === 'AiDisplayImage' &&
      addable.spaceType === 'exterior',
  ) ?? false;

export const sortProjects = (projects: Project[]) =>
  [...projects].sort((a: Project, b: Project) =>
    b?.createdAt?.localeCompare(a?.createdAt),
  );
